import type {
  PendenciaSecretariaItem,
  StatusPendencia,
} from '@/features/secretaria-arbitral/types';

/**
 * Espelho da política do backend (`politicaMovimentacaoPorTipo`), e espelho é a palavra
 * exata: a fronteira real é o servidor, que devolve `TIPO_BLOQUEADO`/`STATUS_BLOQUEADO` em
 * `naoMovimentadas`. Isto existe só para a aba não oferecer um botão que sempre volta como
 * conflito. Se os dois divergirem, quem manda é o backend.
 */
export const STATUS_MOVIMENTAVEIS: readonly StatusPendencia[] = ['ABERTA', 'EXPIRADA'];

/**
 * Tipos cuja RESOLUÇÃO tem efeito colateral fora da própria linha e por isso não se movimenta
 * pela aba: concluso volta pelo árbitro (o flow espera o evento), habilitação de advogado
 * libera os autos, e os de flow (termo de imparcialidade, impugnação, prazo de impugnação, KYC)
 * gravam vínculos que a movimentação genérica não conhece. A autorização de envio de AR é do
 * REQUERENTE: resolvê-la pela aba afirmaria que ele autorizou e pagou a carta. O core bloqueia
 * desde o #1733 e esta lista ficou para trás — a Secretaria via Responder/Resolver e recebia
 * 409 TIPO_BLOQUEADO (ClickUp 86akqyn4c).
 */
export const TIPOS_BLOQUEADOS_NA_ABA: readonly string[] = [
  'CONCLUSO',
  'HABILITACAO_ADVOGADO',
  'TERMO_IMPARCIALIDADE',
  'IMPUGNACAO_ARBITRO',
  'PRAZO_IMPUGNACAO_ARBITRO',
  'KYC',
  'AUTORIZACAO_ENVIO_AR',
];

/** Teto de pendências por ato — espelha `MAX_PENDENCIAS_POR_MOVIMENTACAO` do backend. */
export const MAX_PENDENCIAS_POR_MOVIMENTACAO = 50;

export function podeMovimentar(pendencia: PendenciaSecretariaItem): boolean {
  return (
    STATUS_MOVIMENTAVEIS.includes(pendencia.status) &&
    !TIPOS_BLOQUEADOS_NA_ABA.includes(pendencia.tipo)
  );
}

/**
 * `destinatario: 'SECRETARIA'` é aviso interno (ex.: `CONCLUSO_RESPONDIDO`, ou `OUTROS` de
 * `criarPendenciaAvisoSecretaria`) — a própria Secretaria é quem criou/recebeu o registro que
 * originou o aviso, então não há para quem responder. "Responder" abriria um compositor de
 * texto/anexo sem destinatário real; a única ação que faz sentido é "Resolver" depois de dar o
 * andamento correspondente fora da aba.
 */
export function podeResponder(pendencia: PendenciaSecretariaItem): boolean {
  return pendencia.destinatario !== 'SECRETARIA';
}

/**
 * A contagem regressiva responde "quanto falta para isto ser feito". Numa pendência já
 * resolvida ou encerrada pela sentença a pergunta não existe mais. Um alarme ao lado de um
 * status terminal faz a Secretaria ler como cobrança pendente. A data segue visível na linha de
 * metadados do card — o que sai é só o alarme.
 */
export function prazoAindaCorre(statusDistintos: readonly StatusPendencia[]): boolean {
  return statusDistintos.some(
    (status) => status !== 'RESOLVIDA' && status !== 'ENCERRADA_PELA_SENTENCA',
  );
}
